import type { CSSProperties } from "react";

export type SubjectTreeNodeStyle = "classic" | "acrylic" | "oil" | "watercolor";

export const SUBJECT_TREE_STYLE_STORAGE_KEY = "gistoscope.subjectTree.nodeStyle";
export const DEFAULT_SUBJECT_TREE_STYLE: SubjectTreeNodeStyle = "watercolor";

const SUBJECT_TREE_BRUSH_VARIANT_COUNT = 12;

export const subjectTreeStyleOptions: Array<{ value: SubjectTreeNodeStyle; label: string }> = [
  { value: "classic", label: "Classic" },
  { value: "acrylic", label: "Acrylic" },
  { value: "oil", label: "Oil" },
  { value: "watercolor", label: "Watercolor" },
];

export const isSubjectTreeNodeStyle = (
  value: string | null,
): value is SubjectTreeNodeStyle =>
  value === "classic" || value === "acrylic" || value === "oil" || value === "watercolor";

export const readStoredSubjectTreeStyle = (): SubjectTreeNodeStyle => {
  if (typeof window === "undefined") {
    return DEFAULT_SUBJECT_TREE_STYLE;
  }

  const savedStyle = window.localStorage.getItem(SUBJECT_TREE_STYLE_STORAGE_KEY);
  return isSubjectTreeNodeStyle(savedStyle) ? savedStyle : DEFAULT_SUBJECT_TREE_STYLE;
};

export const persistSubjectTreeStyle = (style: SubjectTreeNodeStyle): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SUBJECT_TREE_STYLE_STORAGE_KEY, style);
};

const stableHash = (value: string): number => {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

const getSubjectStableSeed = (subjectId: number, title = "", depth = 0): number =>
  stableHash(`${subjectId}:${title.trim().toLowerCase()}:${depth}`);

const formatVariantNumber = (variant: number): string => String(variant).padStart(2, "0");

export const getSubjectTreeColorClass = (subjectId: number): string =>
  `subject-tree-node-color-${Math.abs(subjectId) % 8}`;

export const getSubjectTreeBrushVariantClass = (
  subjectId: number,
  title = "",
  depth = 0,
): string => {
  const seed = getSubjectStableSeed(subjectId, title, depth);
  const variant = (seed % SUBJECT_TREE_BRUSH_VARIANT_COUNT) + 1;
  return `subject-tree-brush-variant-${formatVariantNumber(variant)}`;
};

type SubjectTreeBrushProperties = CSSProperties & {
  "--subject-brush-rotate": string;
  "--subject-brush-scale-x": string;
  "--subject-brush-scale-y": string;
  "--subject-brush-offset-x": string;
  "--subject-brush-offset-y": string;
  "--subject-brush-mask": string;
};

const getBrushMaskUrl = (
  styleName: Exclude<SubjectTreeNodeStyle, "classic">,
  variant: number,
): string => `url("/subject-brushes/${styleName}-${formatVariantNumber(variant)}.svg")`;

const getActiveBrushMaskUrl = (style: SubjectTreeNodeStyle, variant: number): string =>
  style === "classic" ? "none" : getBrushMaskUrl(style, variant);

export const getSubjectTreeBrushStyle = (
  subjectId: number,
  title = "",
  depth = 0,
  style: SubjectTreeNodeStyle = DEFAULT_SUBJECT_TREE_STYLE,
): SubjectTreeBrushProperties => {
  const seed = getSubjectStableSeed(subjectId, title, depth);
  const variant = (seed % SUBJECT_TREE_BRUSH_VARIANT_COUNT) + 1;
  const rotation = ((seed % 25) - 12) / 10;
  const scaleX = 0.99 + (((seed >> 5) % 5) / 100);
  const scaleY = 0.99 + (((seed >> 9) % 4) / 100);
  const offsetX = ((seed >> 13) % 5) - 2;
  const offsetY = ((seed >> 17) % 3) - 1;

  return {
    "--subject-brush-rotate": `${rotation.toFixed(1)}deg`,
    "--subject-brush-scale-x": scaleX.toFixed(2),
    "--subject-brush-scale-y": scaleY.toFixed(2),
    "--subject-brush-offset-x": `${offsetX}px`,
    "--subject-brush-offset-y": `${offsetY}px`,
    "--subject-brush-mask": getActiveBrushMaskUrl(style, variant),
  };
};
